import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AnalyticsService, ToastService } from '@common/services';
import { readSupportSession } from '@modules/auth/models';
import { UserService } from '@modules/auth/services';
import { interval, Observable, Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { CopyParts, PHONE_ERROR_HANDLING, PHONE_VERIFICATION_COPY, PhoneErrorHandling, PhoneVerificationCopy, PhoneVerificationRequest } from '../../constants/credit-line.constants';
import { PhoneChannel, PhoneCodeSent, PhoneConfirmed, PhoneErrorCode, PhonePurpose, PhoneVerificationStatus } from '../../models';
import { PhoneVerificationService } from '../../services/phone-verification.service';
import { buildPhoneChannelProps, buildPhoneCodeProps, trackCreditEvent } from '../../utils/credit-analytics.util';
import { PhoneCodeInputComponent } from '../phone-code-input/phone-code-input.component';

/** Paso de la verificación: una sola propiedad, sin booleanos sueltos. */
export type PhoneStep = 'loading' | 'statusFailed' | 'support' | 'confirm' | 'change' | 'code' | 'done';

/** `(verified)`: `alreadyVerified` = entró ya verificado (sin franja ni «Paso 2 de 2» en el formulario). */
export interface PhoneVerifiedEvent {
    alreadyVerified: boolean;
    display: string | null;
    channel: PhoneChannel | null;
}

/** Código enviado y vigente que muestra el paso `code`. */
interface PhoneCodeInFlight {
    purpose: PhonePurpose;
    channel: PhoneChannel;
    display: string;
    resendAvailableAt: string;
}

/**
 * Número del paso de cambio: quita todo lo que no es dígito y un 0 inicial. Acepta 10 dígitos, u 11 con el
 * 0 (con espacios o guiones); devuelve los 10 que viajan, o `null`. La lista de prefijos vive en `api`.
 */
export function normalizeLocalPhone(raw: string | null | undefined): string | null {
    let digits = String(raw ?? '').replace(/\D/g, '');
    if (digits.startsWith('0')) digits = digits.slice(1);
    return digits.length === 10 ? digits : null;
}

/** «0:42»: lo que falta para reenviar, redondeado hacia arriba al segundo. */
export function formatCountdown(ms: number): string {
    const total = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** `0XXX-XXX-XXXX` → `0XXXXXXXXXX`: el destino de un pendiente `change` en el campo del número. */
const displayDigits = (display: string | null | undefined): string => String(display ?? '').replace(/\D/g, '');

/**
 * ADM-813 — paso «Verifica tu teléfono» de la solicitud de Fina Presta (diseño aprobado el 25/10).
 *
 * - Único dueño del estado: pide `GET /users/me/phone-verification` al montarse y hace todas las relecturas;
 *   `line-request-page` solo alterna entre este paso y el formulario con `(verified)`.
 * - Sesión de soporte (`readSupportSession()`): no pide nada y muestra `supportForbidden` (api responde 404
 *   al `GET` y 403 a los `POST`).
 * - Pasos `confirm` → `code` → `done`, con la rama `change` (también la entrada directa con número inválido).
 * - Errores por datos: `PHONE_ERROR_HANDLING`. Excepciones: `PHONE_CODE_INVALID` (intentos) y
 *   `PHONE_CODE_COOLDOWN` (contador). Nunca el `message` del servidor.
 * - La contraseña solo vive en su campo y se vacía tras cada intento. Con `changed: true`, la sesión se
 *   parchea con `UserService.patchSessionUser` (nunca `localStorage` desde aquí).
 * - Eventos `cr_phone_*` después del 200, sin el número, la contraseña ni el código.
 */
@Component({
    selector: 'fina-phone-verification',
    templateUrl: './phone-verification.component.html',
    styleUrls: ['./phone-verification.component.scss'],
})
export class PhoneVerificationComponent implements OnInit, OnDestroy {
    @Output() verified = new EventEmitter<PhoneVerifiedEvent>();
    /** Cambio de paso (`confirm` → `change` → `code` → `done`): el onboarding fiscal esconde su «No es mi número» mientras se cambia el número. */
    @Output() stepChange = new EventEmitter<PhoneStep>();

    /**
     * Piel: `presta` (tarjeta propia de Fina Presta) o `fiscal` (dentro de la fila «Teléfono» de «¿Dónde te
     * avisamos?» del onboarding fiscal: sin tarjeta ni cabecera, con los colores y botones de ese diseño).
     * La lógica es la misma; solo cambia el CSS bajo `.pv--fiscal`.
     */
    @Input() appearance: 'presta' | 'fiscal' = 'presta';

    /**
     * Textos que otro flujo quiere cambiar (recaudos fiscal v2: «Paso» y el porqué de la verificación).
     * Se funden sobre `PHONE_VERIFICATION_COPY`; lo que no se pasa conserva el copy de Fina Presta.
     */
    @Input() set copyOverrides(overrides: Partial<PhoneVerificationCopy> | null | undefined) {
        this.copy = overrides ? { ...PHONE_VERIFICATION_COPY, ...overrides } : PHONE_VERIFICATION_COPY;
    }

    @ViewChild(PhoneCodeInputComponent) codeInput?: PhoneCodeInputComponent;

    copy: PhoneVerificationCopy = PHONE_VERIFICATION_COPY;
    readonly channelOptions: ReadonlyArray<PhoneChannel> = ['sms', 'whatsapp'];

    get step(): PhoneStep {
        return this.stepValue;
    }
    set step(value: PhoneStep) {
        if (value === this.stepValue) return;
        this.stepValue = value;
        this.stepChange.emit(value);
    }
    private stepValue: PhoneStep = 'loading';

    status: PhoneVerificationStatus | null = null;
    /** Canal elegido. WhatsApp preseleccionado, como `Main.dc.html`. */
    channel: PhoneChannel = 'whatsapp';
    sent: PhoneCodeInFlight | null = null;
    /** Un pedido en vuelo: apaga los botones. */
    busy = false;

    /** Aviso del paso (rojo, `role="alert"`). */
    notice: string | null = null;

    // Paso `change`
    phoneInput = '';
    password = '';
    showPassword = false;
    phoneError: string | null = null;
    passwordError: string | null = null;
    /** `PHONE_CODE_COOLDOWN` fuera de `code`: el botón de envío espera hasta esta hora (ISO). */
    sendBlockedUntil: string | null = null;

    // Paso `code`
    code = '';
    codeError: string | null = null;
    codeInvalid = false;
    /** Tras un código incorrecto, la pista pasa de «vence en 10 minutos» a «al quinto intento…». */
    codeHint: 'expiry' | 'attempts' = 'expiry';
    /** El código ya no sirve (vencido o sin intentos): «Verificar» se apaga hasta reenviar. */
    needsNewCode = false;

    /** Reloj de los contadores; el spec lo reemplaza. */
    clock: () => number = () => Date.now();
    now = Date.now();

    /** Turno del último pedido: solo ese libera `busy`. */
    private requestSeq = 0;

    private ticker: Subscription | null = null;
    private destroy$ = new Subject<void>();

    constructor(
        private phoneService: PhoneVerificationService,
        private userService: UserService,
        private toastService: ToastService,
        private analyticsService: AnalyticsService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        if (readSupportSession()) {
            this.step = 'support';
            return;
        }
        this.load(false);
    }

    ngOnDestroy(): void {
        this.stopTicker();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Estado ───────────────────────────────────────────────────────────────────

    /** «Reintentar» tras un `GET` fallido. */
    retry(): void {
        this.step = 'loading';
        this.load(false);
    }

    /**
     * Pide el estado y entra al paso que corresponde. `afterAction`: la relectura vino de un pedido de esta
     * visita (si ya está verificado, se verificó ahora y el formulario lleva la franja). `notice` se pinta
     * después de entrar.
     */
    private load(afterAction: boolean, notice: string | null = null): void {
        const seq = this.startRequest();
        this.phoneService
            .getStatus$()
            .pipe(
                finalize(() => this.endRequest(seq)),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: status => {
                    this.enter(status, afterAction);
                    if (this.step !== 'done') this.notice = notice;
                },
                error: () => {
                    this.stopTicker();
                    this.clearMessages();
                    this.step = 'statusFailed';
                    this.cdr.markForCheck();
                },
            });
    }

    private enter(status: PhoneVerificationStatus, afterAction: boolean): void {
        this.status = status;
        this.clearMessages();
        this.sendBlockedUntil = null;
        if (status.verified) {
            this.finish(status.display, status.channel, !afterAction);
            return;
        }
        if (status.pending) {
            const p = status.pending;
            this.channel = p.channel;
            this.showCode({ purpose: p.purpose, channel: p.channel, display: p.display, resendAvailableAt: p.resendAvailableAt });
            return;
        }
        this.sent = null;
        if (!status.valid) {
            this.openChange('direct');
            return;
        }
        if (status.channels?.[this.channel] === false) this.channel = this.channelOptions.find(c => status.channels?.[c]) ?? this.channel;
        this.stopTicker();
        this.step = 'confirm';
    }

    private finish(display: string | null, channel: PhoneChannel | null, alreadyVerified: boolean): void {
        this.stopTicker();
        this.sent = null;
        this.step = 'done';
        this.verified.emit({ alreadyVerified, display, channel });
    }

    // ── Canal ────────────────────────────────────────────────────────────────────

    channelLabel(channel: PhoneChannel | null | undefined): string {
        return channel === 'sms' ? this.copy.sms : this.copy.whatsapp;
    }

    channelHint(channel: PhoneChannel): string {
        return channel === 'sms' ? this.copy.smsHint : this.copy.whatsappHint;
    }

    get otherChannel(): PhoneChannel {
        return this.channel === 'sms' ? 'whatsapp' : 'sms';
    }

    /** Solo en `confirm`: el servidor dice por qué canales puede salir el número guardado. */
    isChannelDisabled(channel: PhoneChannel): boolean {
        return this.step === 'confirm' && this.status?.channels?.[channel] === false;
    }

    selectChannel(channel: PhoneChannel): void {
        if (this.isChannelDisabled(channel)) return;
        this.channel = channel;
    }

    // ── Contadores ───────────────────────────────────────────────────────────────

    /** Lo que falta para poder reenviar en `code`. */
    get resendRemainingMs(): number {
        return this.remainingUntil(this.sent?.resendAvailableAt);
    }

    /** Lo que falta para poder enviar en `confirm` / `change` tras un `PHONE_CODE_COOLDOWN`. */
    get sendBlockedMs(): number {
        return this.remainingUntil(this.sendBlockedUntil);
    }

    get resendCountdown(): string {
        return formatCountdown(this.resendRemainingMs);
    }

    get sendCountdown(): string {
        return formatCountdown(this.sendBlockedMs);
    }

    get canResend(): boolean {
        return !!this.sent && this.resendRemainingMs <= 0 && !this.busy;
    }

    /** El reloj avanza un segundo (lo llama el `interval`; el spec también). */
    refreshClock(): void {
        this.now = this.clock();
        if (this.resendRemainingMs <= 0 && this.sendBlockedMs <= 0) this.stopTicker();
        // El tic no es un evento del DOM: un host OnPush (paso Confirmar del onboarding fiscal) no repintaría el contador.
        this.cdr.markForCheck();
    }

    private remainingUntil(iso: string | null | undefined): number {
        const at = iso ? Date.parse(iso) : NaN;
        return Number.isFinite(at) ? at - this.now : 0;
    }

    private startTicker(): void {
        this.now = this.clock();
        if (this.ticker) return;
        this.ticker = interval(1000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.refreshClock());
    }

    private stopTicker(): void {
        this.ticker?.unsubscribe();
        this.ticker = null;
    }

    // ── Paso `confirm` ───────────────────────────────────────────────────────────

    get canSend(): boolean {
        return !this.busy && this.sendBlockedMs <= 0 && !this.isChannelDisabled(this.channel);
    }

    sendCode(): void {
        if (!this.canSend) return;
        this.notice = null;
        this.run(this.phoneService.send$(this.channel), 'send', sent => this.onSent(sent));
    }

    // ── Paso `change` ────────────────────────────────────────────────────────────

    /** El número guardado no es un celular (o no hay): se entra directo a `change`, sin «Cancelar». */
    get isInvalidEntry(): boolean {
        return this.status?.valid === false;
    }

    /** El número crudo que se nombra en el aviso; sin número, no hay aviso. */
    get invalidRaw(): string | null {
        const raw = String(this.status?.phone ?? '').trim();
        return this.isInvalidEntry && raw ? raw : null;
    }

    /** Con el número guardado inválido no hay paso anterior, salvo un código vigente al que volver. */
    get canCancelChange(): boolean {
        return !this.isInvalidEntry || !!this.sent;
    }

    get canSubmitChange(): boolean {
        return !this.busy && this.sendBlockedMs <= 0 && !!this.phoneInput.trim() && !!this.password;
    }

    /** «No es mi número» (desde `confirm` o `code`). */
    notMine(): void {
        this.openChange('user');
    }

    /**
     * Abre el paso de cambio. `direct`: el número guardado no sirve. `reauth`: el reenvío de un cambio pidió
     * la contraseña otra vez. Emite `cr_phone_change_started` al abrirlo por el usuario o por la entrada directa.
     */
    private openChange(source: 'user' | 'direct' | 'reauth'): void {
        const wasOpen = this.step === 'change';
        // El reloj sigue: un `PHONE_CODE_COOLDOWN` pendiente también rige en `change`, y `refreshClock` lo
        // detiene solo cuando no queda ninguna espera (QA 002#1).
        this.startTicker();
        this.step = 'change';
        this.phoneError = null;
        this.passwordError = null;
        this.password = '';
        this.showPassword = false;
        // Con un cambio pendiente, se precarga su destino (el número nuevo).
        this.phoneInput = this.sent?.purpose === 'change' ? displayDigits(this.sent.display) : source === 'reauth' ? this.phoneInput : '';
        if (!wasOpen && source !== 'reauth') trackCreditEvent(this.analyticsService, 'phoneChangeStarted', buildPhoneChannelProps(this.channel));
    }

    /** «Cancelar»: vuelve al código vigente o al número guardado. */
    cancelChange(): void {
        this.password = '';
        this.phoneError = null;
        this.passwordError = null;
        this.notice = null;
        if (this.sent) {
            // Las casillas vuelven a nacer vacías: el código escrito antes ya no está a la vista (QA 001#1).
            this.code = '';
            if (!this.needsNewCode) {
                this.codeError = null;
                this.codeInvalid = false;
            }
            this.step = 'code';
            this.startTicker();
            return;
        }
        this.step = 'confirm';
        this.startTicker();
    }

    onPhoneInput(value: string): void {
        this.phoneInput = value ?? '';
        this.phoneError = null;
    }

    onPasswordInput(value: string): void {
        this.password = value ?? '';
        this.passwordError = null;
    }

    submitChange(): void {
        if (!this.canSubmitChange) return;
        this.notice = null;
        this.phoneError = null;
        this.passwordError = null;
        const phone = normalizeLocalPhone(this.phoneInput);
        if (!phone) {
            this.phoneError = this.copy.phoneIncomplete;
            return;
        }
        const password = this.password;
        // La contraseña no se guarda: se vacía en cada intento, salga bien o mal.
        this.password = '';
        this.run(this.phoneService.change$({ phone, channel: this.channel, password }), 'change', sent => this.onSent(sent));
    }

    // ── Paso `code` ──────────────────────────────────────────────────────────────

    get codeSubtitle(): CopyParts | null {
        return this.sent ? this.copy.codeSubtitle(this.channelLabel(this.sent.channel), this.sent.display) : null;
    }

    get canVerify(): boolean {
        return this.code.length === 6 && !this.needsNewCode && !this.busy;
    }

    onCodeChanged(value: string): void {
        this.code = value;
        if (!this.needsNewCode) {
            this.codeError = null;
            this.codeInvalid = false;
        }
    }

    onCodeCompleted(value: string): void {
        this.code = value;
        this.verifyCode();
    }

    verifyCode(): void {
        if (!this.canVerify) return;
        this.notice = null;
        this.run(this.phoneService.confirm$(this.code), 'confirm', data => this.onConfirmed(data));
    }

    /** «Reenviar por {canal}» o «Enviar por {otro canal}». */
    resend(channel: PhoneChannel): void {
        if (!this.canResend) return;
        this.notice = null;
        this.channel = channel;
        this.run(this.phoneService.resend$(channel), 'resend', sent => this.onSent(sent));
    }

    // ── Respuestas ───────────────────────────────────────────────────────────────

    private run<T>(request$: Observable<T>, request: PhoneVerificationRequest, onOk: (data: T) => void): void {
        const seq = this.startRequest();
        request$
            .pipe(
                // El interceptor 423 de mora completa sin emitir: el botón se libera igual.
                finalize(() => this.endRequest(seq)),
                takeUntil(this.destroy$),
            )
            .subscribe({ next: onOk, error: (err: any) => this.handleError(err, request) });
    }

    /** Un pedido nuevo (`run` o `load`) toma el turno de `busy`. */
    private startRequest(): number {
        this.busy = true;
        return ++this.requestSeq;
    }

    /**
     * Solo el último pedido libera `busy`. En RxJS el `error` corre antes que `finalize`: si el error lanzó
     * una relectura, el `finalize` del pedido fallido no apaga `busy` con el `GET` todavía en vuelo (QA 002#2).
     * Siempre marca la rama para la detección de cambios: las respuestas HTTP no son eventos del DOM y, bajo
     * un host OnPush (paso Confirmar del onboarding fiscal), el paso nuevo no se pintaba y el spinner no salía.
     */
    private endRequest(seq: number): void {
        if (seq === this.requestSeq) this.busy = false;
        this.cdr.markForCheck();
    }

    private onSent(sent: PhoneCodeSent): void {
        trackCreditEvent(this.analyticsService, 'phoneCodeSent', buildPhoneCodeProps(sent.channel, sent.purpose));
        this.channel = sent.channel;
        this.password = '';
        this.sendBlockedUntil = null;
        this.showCode({ purpose: sent.purpose, channel: sent.channel, display: sent.display, resendAvailableAt: sent.resendAvailableAt });
    }

    private showCode(sent: PhoneCodeInFlight): void {
        const wasCode = this.step === 'code';
        this.sent = sent;
        this.step = 'code';
        this.code = '';
        this.codeError = null;
        this.codeInvalid = false;
        this.codeHint = 'expiry';
        this.needsNewCode = false;
        this.startTicker();
        // Al reenviar, las casillas ya existen: se vacían. Si recién se entra, nacen vacías.
        if (wasCode) this.codeInput?.reset();
    }

    private onConfirmed(data: PhoneConfirmed): void {
        const purpose = this.sent?.purpose ?? (data.changed ? 'change' : 'verify');
        trackCreditEvent(this.analyticsService, 'phoneCodeVerified', buildPhoneCodeProps(data.channel, purpose));
        if (data.changed) this.userService.patchSessionUser({ phone: data.phone, callingCode: data.callingCode });
        this.finish(data.display, data.channel, false);
    }

    private handleError(err: any, request: PhoneVerificationRequest): void {
        const body = err?.error;
        const code = typeof body?.code === 'string' ? (body.code as PhoneErrorCode) : null;
        if (code === 'PHONE_CODE_INVALID') {
            const left = Number(body?.attemptsRemaining);
            if (Number.isFinite(left) && left > 0) {
                this.codeError = this.copy.codeInvalid(left);
                this.codeInvalid = true;
                this.codeHint = 'attempts';
                // Las casillas se vacían: sin esto «Verificar» reenviaría el código equivocado (QA 001#1).
                this.code = '';
                this.codeInput?.reset();
                return;
            }
            // El quinto incorrecto llega con 0: igual que sin intentos.
            this.applyHandling(PHONE_ERROR_HANDLING.PHONE_CODE_MAX_ATTEMPTS as PhoneErrorHandling, request);
            return;
        }
        if (code === 'PHONE_CODE_COOLDOWN') {
            this.applyCooldown(body);
            return;
        }
        const handling = (code && PHONE_ERROR_HANDLING[code]) || { copy: 'genericError', target: 'notice' };
        this.applyHandling(handling as PhoneErrorHandling, request);
    }

    /** Reajusta el contador: en `code`, el de reenvío; en `confirm` / `change`, el del botón de envío. */
    private applyCooldown(body: any): void {
        const fromBody = typeof body?.resendAvailableAt === 'string' && Number.isFinite(Date.parse(body.resendAvailableAt)) ? body.resendAvailableAt : null;
        const wait = Number(body?.waitSeconds);
        const until = fromBody ?? new Date(this.clock() + (Number.isFinite(wait) && wait > 0 ? wait : 60) * 1000).toISOString();
        if (this.step === 'code' && this.sent) this.sent = { ...this.sent, resendAvailableAt: until };
        else this.sendBlockedUntil = until;
        this.startTicker();
    }

    private applyHandling(handling: PhoneErrorHandling, request: PhoneVerificationRequest): void {
        const message = handling.copy ? this.copy[handling.copy] : null;
        const reread = handling.reread === true || (Array.isArray(handling.reread) && handling.reread.includes(request));
        if (handling.target === 'toast' && message) this.toastService.error(this.copy.verifyTitle, message);
        // El servidor inutilizó el código pendiente: «Cancelar» ya no puede volver a él (QA 002#3).
        if (handling.discardCode?.includes(request)) this.sent = null;
        if (reread) {
            this.load(true, handling.target === 'notice' ? message : null);
            return;
        }
        if (handling.step === 'change') this.openChange('reauth');
        if (handling.newCode) {
            this.needsNewCode = true;
            this.codeInvalid = false;
        }
        if (!message || handling.target === 'toast') return;
        const target = this.fieldTarget(handling.target);
        if (target === 'phone') this.phoneError = message;
        else if (target === 'password') this.passwordError = message;
        else if (target === 'code') this.codeError = message;
        else this.notice = message;
    }

    /** Un destino de campo que no está a la vista cae al aviso del paso. */
    private fieldTarget(target: PhoneErrorHandling['target']): PhoneErrorHandling['target'] {
        if ((target === 'phone' || target === 'password') && this.step !== 'change') return 'notice';
        if (target === 'code' && this.step !== 'code') return 'notice';
        return target;
    }

    private clearMessages(): void {
        this.notice = null;
        this.phoneError = null;
        this.passwordError = null;
        this.codeError = null;
        this.codeInvalid = false;
    }
}
